/**
 * Recovering a session whose JavaScript was deleted out from under it.
 *
 * Every deploy renames the code-split chunks, and `aws s3 sync --delete`
 * removes the previous ones. A browser that already has the app open still
 * holds the old `index.html`, so the next lazy route it visits asks for a file
 * that is no longer in the bucket. The 404 is answered by the SPA fallback —
 * `index.html` — and the browser parses `<!DOCTYPE html>` as JavaScript.
 *
 * Nothing is wrong with the session or the deployed build: it is simply running
 * code that no longer exists. The fix is to reload into the build that does,
 * which is why this is a reload rather than an error screen (PLAT-6541).
 */

/**
 * Signatures that mean "that chunk is gone" and nothing else. Safe to act on
 * alone, because no other failure produces them.
 */
const CHUNK_FETCH_PATTERNS = [
  // webpack, which is what `react-scripts` builds with today.
  /loading (css )?chunk [^\s]+ failed/i,
  // A dynamic import that cannot be fetched — how a deleted chunk surfaces
  // once the app builds with Vite (PLAT-6366). Every engine words it
  // differently and each one strands its own users if it is missing:
  // Chromium,
  /failed to fetch dynamically imported module/i,
  // Firefox (SpiderMonkey's JSMSG_DYNAMIC_IMPORT_FAILED),
  /error loading dynamically imported module/i,
  // and Safari.
  /importing a module script failed/i,
  // Vite's preload helper, when a stylesheet the chunk depends on fails to
  // load. The CSS counterpart of the imports above (Sentry WEB-FE-2Z).
  /unable to preload css/i,
];

/**
 * The signature we actually see in production, and the one that needs care.
 *
 * When the chunk 404s, S3 answers with `index.html`, and the browser reports
 * choking on the doctype (Sentry WEB-FE-2T). Chrome and Safari say
 * `Unexpected token '<'`; older engines omit the quotes; Firefox phrases it
 * differently.
 */
const DOCTYPE_PARSE_PATTERNS = [
  /unexpected token '?</i,
  /expected expression, got '?</i,
];

/**
 * The same wording is produced by `JSON.parse` when a fetch that expected JSON
 * is handed an HTML error page — a 502, a CloudFront error, a session-expired
 * redirect to a login page. That is a backend incident, not a stale bundle, and
 * reloading through it would hide the outage behind a refresh cycle. So the
 * doctype signal only counts when the failure was not a JSON parse.
 *
 * Deliberately not applied to CHUNK_FETCH_PATTERNS: a real Vite chunk can be
 * named something like `/assets/json-utils-a1b2.js`, and excluding it for
 * containing "json" would strand exactly the sessions this exists to rescue.
 */
const JSON_PARSE_HINT = /json/i;

export const isStaleBuildError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  // webpack names this one, which is more reliable than matching prose.
  if ((error as { name?: unknown }).name === 'ChunkLoadError') {
    return true;
  }

  const message = (error as { message?: unknown }).message;
  const text = typeof message === 'string' ? message : String(error);

  if (CHUNK_FETCH_PATTERNS.some((pattern) => pattern.test(text))) {
    return true;
  }

  return (
    DOCTYPE_PARSE_PATTERNS.some((pattern) => pattern.test(text)) &&
    !JSON_PARSE_HINT.test(text)
  );
};

const RELOAD_MARKER = 'martec.staleBuildReloadedAt';

/**
 * Long enough that a reload which did not fix anything cannot become a loop,
 * short enough that a user who meets a second real deploy later in the same
 * session is still rescued.
 */
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Reload once, and only once, per cooldown window.
 *
 * The marker lives in sessionStorage rather than module state because the thing
 * it guards against is a reload, which re-evaluates every module.
 *
 * If sessionStorage is unavailable — Safari private mode, blocked site data —
 * we cannot prove we have not already reloaded, so we do not, and the caller is
 * left showing its error UI. A loop is worse than an error screen.
 *
 * Returns whether a reload was started.
 */
export const reloadForStaleBuild = (): boolean => {
  let reloadedAt: string | null;

  try {
    reloadedAt = window.sessionStorage.getItem(RELOAD_MARKER);
  } catch {
    return false;
  }

  // A marker we cannot make sense of — corrupted, or written by a clock that
  // has since moved backwards — must not be able to suppress the reload
  // forever, so anything not a sane elapsed time is treated as "no marker".
  const elapsed = Date.now() - Number(reloadedAt);
  if (
    reloadedAt !== null &&
    Number.isFinite(elapsed) &&
    elapsed >= 0 &&
    elapsed < RELOAD_COOLDOWN_MS
  ) {
    return false;
  }

  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch {
    return false;
  }

  window.location.reload();
  return true;
};
